import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, writeBatch, serverTimestamp, query, where, getDoc } from 'firebase/firestore';
import { getDancerAgeCategoryForYear, wasInCorrectCategoryAtCompetition } from './ageCategoryUtils';

/**
 * Global Cache Calculator for Frontend
 * This runs in the browser using the same Firebase auth as the rest of the app
 */

class GlobalCacheCalculator {
  constructor() {
    this.startTime = null;
    this.dancerAgeCategoriesByYear = new Map(); // dancerName -> { year -> ageCategory }
    this.progress = {
      categories: 0,
      dancers: 0,
      currentTask: ''
    };
    this.listeners = [];
  }

  addProgressListener(callback) {
    this.listeners.push(callback);
  }

  updateProgress(update) {
    this.progress = { ...this.progress, ...update };
    this.listeners.forEach(callback => callback(this.progress));
  }

  /**
   * Load all studio transfers for batch processing
   */
  async loadAllStudioTransfers() {
    try {
      const transfersSnapshot = await getDocs(
        query(collection(db, 'studioTransfers'), where('status', '==', 'active'))
      );
      
      const transfersByDancer = new Map();
      
      transfersSnapshot.docs.forEach(doc => {
        const transfer = {
          id: doc.id,
          ...doc.data(),
          transferDate: doc.data().transferDate.toDate()
        };
        
        if (!transfersByDancer.has(transfer.dancerName)) {
          transfersByDancer.set(transfer.dancerName, []);
        }
        transfersByDancer.get(transfer.dancerName).push(transfer);
      });
      
      // Sort transfers for each dancer by date (most recent first)
      transfersByDancer.forEach(transfers => {
        transfers.sort((a, b) => b.transferDate - a.transferDate);
      });
      
      return transfersByDancer;
    } catch (error) {
      console.error('❌ Error loading studio transfers:', error);
      return new Map();
    }
  }

  /**
   * Resolve current studio for a dancer using pre-loaded transfer data
   */
  resolveCurrentStudioFromTransfers(dancerName, transfersByDancer, competitionStudioMap) {
    // Check if dancer has any transfers
    const transfers = transfersByDancer.get(dancerName);
    if (transfers && transfers.length > 0) {
      // Use the "toStudio" of the most recent transfer
      return transfers[0].toStudio;
    }
    
    // No transfers, use most recent studio from competitions
    return competitionStudioMap.get(dancerName) || null;
  }

  /**
   * Build map of dancer to most recent studio from competitions
   */
  async buildCompetitionStudioMap() {
    const studioMap = new Map();
    
    try {
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      
      competitionsSnapshot.forEach((doc) => {
        const competition = doc.data();
        if (competition.results) {
          const competitionDate = competition.timestamp && competition.timestamp.toDate ? 
            competition.timestamp.toDate() : new Date(competition.timestamp || competition.date);
            
          competition.results.forEach(result => {
            if (result.dancerName && result.studio) {
              const currentEntry = studioMap.get(result.dancerName);
              if (!currentEntry || competitionDate > currentEntry.date) {
                studioMap.set(result.dancerName, {
                  studio: result.studio,
                  date: competitionDate
                });
              }
            }
          });
        }
      });
      
      // Convert to simple studio names
      const simpleMap = new Map();
      studioMap.forEach((entry, dancerName) => {
        simpleMap.set(dancerName, entry.studio);
      });
      
      return simpleMap;
    } catch (error) {
      console.error('❌ Error building competition studio map:', error);
      return new Map();
    }
  }

  /**
   * Get dancer's age category for a specific year (with caching)
   */
  async getDancerAgeCategoryForYearCached(dancerName, year) {
    // Check cache first
    if (this.dancerAgeCategoriesByYear.has(dancerName)) {
      const yearCache = this.dancerAgeCategoriesByYear.get(dancerName);
      if (yearCache[year] !== undefined) {
        return yearCache[year];
      }
    }
    
    // Fetch from database
    const ageCategory = await getDancerAgeCategoryForYear(dancerName, year);
    
    // Cache the result
    if (!this.dancerAgeCategoriesByYear.has(dancerName)) {
      this.dancerAgeCategoriesByYear.set(dancerName, {});
    }
    this.dancerAgeCategoriesByYear.get(dancerName)[year] = ageCategory;
    
    return ageCategory;
  }

  /**
   * Calculate rankings for all dancers in a specific category
   */
  async calculateCategoryRankings(discipline, category, dance = null) {
    const categoryName = `${discipline} - ${category}${dance ? ` (${dance})` : ''}`;
    this.updateProgress({ currentTask: `Calculating ${categoryName}` });
    
    const rankingCutoff = new Date();
    rankingCutoff.setDate(rankingCutoff.getDate() - 365);
    
    const dancerResults = new Map();
    
    try {
      // Query competitions collection
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      
      for (const docSnapshot of competitionsSnapshot.docs) {
        const comp = docSnapshot.data();
        const compId = docSnapshot.id;
        const multiplier = comp.multiplier || 1;
        
        // Check if this competition matches our criteria
        const matchesDiscipline = comp.discipline === discipline;
        const matchesCategory = comp.category === category;
        const matchesDance = !dance || comp.dance === dance;
        
        if (matchesDiscipline && matchesCategory && matchesDance && comp.results) {
          const resultDate = comp.timestamp && comp.timestamp.toDate ? comp.timestamp.toDate() : new Date(comp.timestamp);
          const isWithin365Days = resultDate >= rankingCutoff;
          const competitionYear = resultDate.getFullYear();
          
          if (isWithin365Days) {
            for (const result of comp.results) {
              if (!result.excluded && !result.autoExcluded && !result.invalid && !result.withdrawn && result.dancerName) {
                
                // 🎯 Age Category Validation: Filter out dancers who competed in a category LOWER than their actual age
                const dancerAgeCategoryAtCompetition = await this.getDancerAgeCategoryForYearCached(result.dancerName, competitionYear);
                if (!wasInCorrectCategoryAtCompetition(category, dancerAgeCategoryAtCompetition)) {
                  // Skip this result - dancer competed in a lower category than allowed
                  continue;
                }
                
                const points = Math.round((result.points || 0) * multiplier);
                
                if (!dancerResults.has(result.dancerName)) {
                  dancerResults.set(result.dancerName, []);
                }
                
                dancerResults.get(result.dancerName).push({
                  compId,
                  points,
                  timestamp: comp.timestamp,
                  place: result.place,
                  resultDate: resultDate,
                  studio: result.studio || null // Include studio information
                });
              }
            }
          }
        }
      }
      
      // Calculate top 6 points for each dancer
      const dancerRankings = [];
      
      for (const [dancerName, results] of dancerResults.entries()) {
        const sortedResults = results.sort((a, b) => b.points - a.points);
        const top6Results = sortedResults.slice(0, 6);
        const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
        
        if (totalPoints > 0) {
          // Calculate Star Ranking details for this dancer
          const starRankingData = await this.calculateStarRankingDetails(
            dancerName, discipline, category, dance, results, totalPoints
          );
          
          dancerRankings.push({
            dancerName,
            totalPoints,
            top6CompIds: top6Results.map(r => r.compId),
            resultsCount: results.length,
            results: results, // Include all results with studio information
            ...starRankingData // Include full Star Ranking calculations
          });
        }
      }
      
      // Sort and assign ranks
      dancerRankings.sort((a, b) => b.totalPoints - a.totalPoints);
      
      let currentRank = 1;
      for (let i = 0; i < dancerRankings.length; i++) {
        if (i > 0 && dancerRankings[i].totalPoints !== dancerRankings[i-1].totalPoints) {
          currentRank = i + 1;
        }
        dancerRankings[i].currentRank = currentRank;
      }
      
      console.log(`✅ ${dancerRankings.length} dancers ranked in ${categoryName}`);
      
      return {
        discipline,
        category,
        dance,
        rankings: dancerRankings,
        totalDancers: dancerRankings.length,
        lastCalculated: serverTimestamp()
      };
      
    } catch (error) {
      console.error(`❌ Error calculating rankings for ${categoryName}:`, error);
      return null;
    }
  }

  /**
   * Calculate comprehensive Star Ranking details for a dancer
   */
  async calculateStarRankingDetails(dancerName, discipline, category, dance, results, totalPoints) {
    try {
      // Calculate historical rankings using the same logic as DancerProfile
      const historicalData = await this.calculateHistoricalRankings(discipline, category, dance, dancerName);
      
      return {
        // Current rank will be set in the main ranking loop
        activePoints: totalPoints, // Points from last 365 days
        currentDaysHeld: historicalData.currentDaysHeld || 0,
        bestRank: historicalData.bestRank || null,
        bestDaysHeld: historicalData.bestDaysHeld || 0,
        lastCalculated: new Date(),
        hasStarRanking: totalPoints > 0
      };
      
    } catch (error) {
      console.error(`❌ Error calculating Star Ranking details for ${dancerName}:`, error);
      return {
        activePoints: totalPoints,
        currentDaysHeld: 0,
        bestRank: null,
        bestDaysHeld: 0,
        lastCalculated: new Date(),
        hasStarRanking: false
      };
    }
  }

  /**
   * Calculate historical rankings using EXACT same logic as DancerProfile
   */
  async calculateHistoricalRankings(discipline, category, dance, dancerName) {
    try {
      // Get all competitions in chronological order
      const allCompetitions = await this.getChronologicalCompetitions(discipline, category, dance);
      
      if (allCompetitions.length === 0) {
        return { bestRank: null, bestDaysHeld: 0, currentDaysHeld: 0 };
      }
      
      // Calculate rank progression over time (EXACT same logic as DancerProfile)
      const rankHistory = this.calculateRankProgression(allCompetitions, dancerName);
      
      // Find best rank and calculate days held (EXACT same logic as DancerProfile)
      const bestRankData = this.findBestRankAndDays(rankHistory);
      const currentRankDays = this.calculateCurrentRankDays(rankHistory);
      
      return {
        bestRank: bestRankData.bestRank,
        bestDaysHeld: bestRankData.maxDaysHeld,
        currentDaysHeld: currentRankDays
      };
      
    } catch (error) {
      console.error(`❌ Error calculating historical rankings for ${dancerName}:`, error);
      return { bestRank: null, bestDaysHeld: 0, currentDaysHeld: 0 };
    }
  }

  /**
   * Get all competitions in chronological order (EXACT same logic as DancerProfile)
   */
  async getChronologicalCompetitions(discipline, category, dance) {
    const competitions = [];
    
    try {
      // Query competitions collection (matches DancerProfile logic)
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      
      for (const docSnapshot of competitionsSnapshot.docs) {
        const comp = docSnapshot.data();
        
        // Check if this competition matches our criteria (EXACT same logic)
        const matchesDiscipline = comp.discipline === discipline;
        const matchesCategory = comp.category === category;
        const matchesDance = !dance || comp.dance === dance;
        
        if (matchesDiscipline && matchesCategory && matchesDance && comp.results && comp.timestamp) {
          const resultDate = comp.timestamp && comp.timestamp.toDate ? comp.timestamp.toDate() : new Date(comp.timestamp);
          
          competitions.push({
            id: docSnapshot.id,
            name: comp.name || docSnapshot.id,
            timestamp: resultDate,
            results: comp.results.map(r => ({
              dancerName: r.dancerName,
              points: Math.round((r.points || 0) * (comp.multiplier || 1)),
              place: r.place
            })),
            multiplier: comp.multiplier || 1
          });
        }
      }
      
      // Sort by timestamp (oldest first) - EXACT same as DancerProfile
      return competitions.sort((a, b) => a.timestamp - b.timestamp);
      
    } catch (error) {
      console.error('❌ Error getting chronological competitions:', error);
      return [];
    }
  }

  /**
   * Calculate rank progression over time (EXACT same logic as DancerProfile)
   */
  calculateRankProgression(competitions, targetDancer) {
    const rankHistory = [];
    const dancerData = new Map(); // Track cumulative results for each dancer
    
    for (const competition of competitions) {
      // Update each dancer's results with this competition
      for (const result of competition.results) {
        if (!dancerData.has(result.dancerName)) {
          dancerData.set(result.dancerName, []);
        }
        dancerData.get(result.dancerName).push({
          points: result.points,
          timestamp: competition.timestamp,
          compId: competition.id
        });
      }
      
      // Calculate rankings after this competition
      const allDancers = Array.from(dancerData.entries()).map(([name, results]) => {
        // Only count results from last 365 days from this competition date
        const cutoffDate = new Date(competition.timestamp);
        cutoffDate.setDate(cutoffDate.getDate() - 365);
        
        const validResults = results.filter(r => r.timestamp >= cutoffDate);
        const sortedResults = validResults.sort((a, b) => b.points - a.points);
        const top6Results = sortedResults.slice(0, 6);
        const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
        
        return { name, totalPoints, resultCount: validResults.length };
      });
      
      // Sort by points to get rankings
      allDancers.sort((a, b) => b.totalPoints - a.totalPoints);
      
      // Find target dancer's rank
      const targetDancerIndex = allDancers.findIndex(d => d.name === targetDancer);
      if (targetDancerIndex !== -1) {
        const rank = targetDancerIndex + 1;
        const totalPoints = allDancers[targetDancerIndex].totalPoints;
        
        rankHistory.push({
          date: competition.timestamp,
          rank: rank,
          totalPoints: totalPoints,
          competitionId: competition.id,
          totalDancers: allDancers.filter(d => d.totalPoints > 0).length
        });
      }
    }
    
    return rankHistory;
  }

  /**
   * Find best rank and calculate maximum consecutive days held (EXACT same logic as DancerProfile)
   */
  findBestRankAndDays(rankHistory) {
    if (rankHistory.length === 0) {
      return { bestRank: null, maxDaysHeld: 0 };
    }
    
    let bestRank = Math.min(...rankHistory.map(r => r.rank));
    let maxDaysHeld = 0;
    let streakStartDate = null;
    
    for (let i = 0; i < rankHistory.length; i++) {
      const current = rankHistory[i];
      
      if (current.rank === bestRank) {
        if (streakStartDate === null) {
          // Start of a new streak at best rank
          streakStartDate = current.date;
        }
        // Continue the streak - we stay at best rank
      } else {
        // Rank changed - end current streak if we were in one
        if (streakStartDate !== null) {
          // Calculate total days from start of streak to the current competition (where rank changed)
          const currentCompetition = rankHistory[i]; // The competition where rank changed
          const streakDays = Math.ceil((currentCompetition.date - streakStartDate) / (1000 * 60 * 60 * 24));
          
          // If start and current are on the same day, they held it for 1 day minimum
          const adjustedDays = Math.max(streakDays, 1);
          
          maxDaysHeld = Math.max(maxDaysHeld, adjustedDays);
          streakStartDate = null;
        }
      }
    }
    
    // Handle case where streak continues to the end (still at best rank)
    if (streakStartDate !== null) {
      const ongoingDays = Math.ceil((new Date() - streakStartDate) / (1000 * 60 * 60 * 24));
      maxDaysHeld = Math.max(maxDaysHeld, Math.max(ongoingDays, 1)); // Minimum 1 day
    }
    
    return { bestRank, maxDaysHeld };
  }

  /**
   * Calculate current rank consecutive days (EXACT same logic as DancerProfile)
   */
  calculateCurrentRankDays(rankHistory) {
    if (rankHistory.length === 0) {
      return 0;
    }
    
    const currentRank = rankHistory[rankHistory.length - 1].rank;
    let streakStartDate = null;
    
    // Go backwards from the most recent competition to find where current rank started
    for (let i = rankHistory.length - 1; i >= 0; i--) {
      const competition = rankHistory[i];
      
      if (competition.rank === currentRank) {
        // Still the same rank, continue going back
        streakStartDate = competition.date;
      } else {
        // Different rank found, stop here
        break;
      }
    }
    
    // Calculate days from when current rank started to today
    if (streakStartDate !== null) {
      const today = new Date();
      const consecutiveDays = Math.ceil((today - streakStartDate) / (1000 * 60 * 60 * 24));
      return Math.max(consecutiveDays, 7); // Minimum 7 days (typical competition interval)
    }
    
    return 0;
  }

  /**
   * Discover all unique discipline/category/dance combinations
   */
  async getAllCategoryCombinations() {
    this.updateProgress({ currentTask: 'Discovering categories...' });
    
    const combinations = new Set();
    
    try {
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      
      competitionsSnapshot.docs.forEach(docSnapshot => {
        const comp = docSnapshot.data();
        if (comp.discipline && comp.category) {
          if (comp.discipline === 'Latin' && comp.dance) {
            combinations.add(`${comp.discipline}|${comp.category}|${comp.dance}`);
          } else {
            combinations.add(`${comp.discipline}|${comp.category}|`);
          }
        }
      });
      
      const combinationsList = Array.from(combinations).map(combo => {
        const [discipline, category, dance] = combo.split('|');
        return { discipline, category, dance: dance || null };
      });
      
      console.log(`✅ Found ${combinationsList.length} combinations`);
      return combinationsList;
      
    } catch (error) {
      console.error('❌ Error discovering combinations:', error);
      return [];
    }
  }

  /**
   * Calculate comprehensive profile for a single dancer
   */
  /**
   * Check if a dancer has won any Eternal Cup and collect all years/categories
   */
  async checkEternalCupWinner(dancerName) {
    try {
      const wins = []; // Collect all wins (year and category)
      
      // Check eternalCupWinners collection for all available years
      const winnersSnapshot = await getDocs(collection(db, 'eternalCupWinners'));
      
      for (const doc of winnersSnapshot.docs) {
        const yearData = doc.data();
        const year = doc.id;
        
        if (yearData.winners) {
          // Check all categories in this year's winners
          for (const [categoryKey, categoryWinners] of Object.entries(yearData.winners)) {
            if (Array.isArray(categoryWinners)) {
              // Find if dancer won (rank === 1) in this category
              const winnerEntry = categoryWinners.find(winner => 
                winner.dancerName === dancerName && winner.rank === 1
              );
              
              if (winnerEntry) {
                wins.push({
                  year: parseInt(year),
                  category: categoryKey
                });
              }
            }
          }
        }
      }
      
      if (wins.length > 0) {
        return {
          isWinner: true,
          wins: wins // Array of all wins with year and category
        };
      }
      
      return { isWinner: false, wins: [] };
    } catch (error) {
      console.error(`❌ Error checking Eternal Cup winner for ${dancerName}:`, error);
      return { isWinner: false, wins: [] };
    }
  }

  /**
   * Calculate achievement badges for a dancer based on their results
   */
  calculateAchievementBadges(results) {
    if (!results || results.length === 0) {
      return [];
    }

    const badges = [];

    // 1. Eternal Cup Winner - Count wins
    const eternalCupWins = results.filter(r => 
      r.competitionName && r.competitionName.toLowerCase().includes('eternal cup') && 
      String(r.place) === '1'
    ).length;
    
    if (eternalCupWins > 0) {
      badges.push({
        id: 'eternal-cup',
        icon: '🏆',
        name: 'Eternal Cup Winner',
        count: eternalCupWins,
        color: '#FFD700'
      });
    }

    // 2. Gold Medals - Count 1st place finishes
    const goldMedals = results.filter(r => String(r.place) === '1').length;
    if (goldMedals > 0) {
      badges.push({
        id: 'gold-medal',
        icon: '🥇',
        name: 'Gold Medal',
        count: goldMedals,
        color: '#FFD700'
      });
    }

    // 3. Silver Medals - Count 2nd place finishes
    const silverMedals = results.filter(r => String(r.place) === '2').length;
    if (silverMedals > 0) {
      badges.push({
        id: 'silver-medal',
        icon: '🥈',
        name: 'Silver Medal',
        count: silverMedals,
        color: '#C0C0C0'
      });
    }

    // 4. Bronze Medals - Count 3rd place finishes
    const bronzeMedals = results.filter(r => String(r.place) === '3').length;
    if (bronzeMedals > 0) {
      badges.push({
        id: 'bronze-medal',
        icon: '🥉',
        name: 'Bronze Medal',
        count: bronzeMedals,
        color: '#CD7F32'
      });
    }

    // 5. Active - Calculate years active
    if (results.length > 0) {
      const dates = results
        .map(r => {
          if (r.date) {
            if (r.date.toDate) return r.date.toDate();
            if (r.date instanceof Date) return r.date;
            return new Date(r.date);
          }
          return null;
        })
        .filter(d => d !== null);
      
      if (dates.length > 0) {
        const oldestDate = new Date(Math.min(...dates.map(d => d.getTime())));
        const yearsActive = Math.floor((new Date() - oldestDate) / (365.25 * 24 * 60 * 60 * 1000));
        
        if (yearsActive >= 1) {
          badges.push({
            id: 'active',
            icon: '⏰',
            name: 'Active',
            count: yearsActive,
            suffix: yearsActive === 1 ? 'year' : 'years',
            color: '#4CAF50'
          });
        }
      }
    }

    // 6. Diverse - Multi-discipline check (only Latin, Contemporary, Hip Hop, Commercial count as separate disciplines)
    const disciplines = [...new Set(results.map(r => r.discipline).filter(Boolean))];
    const validDisciplines = ['Latin', 'Contemporary', 'Hip Hop', 'Commercial'];
    const competedDisciplines = disciplines.filter(d => 
      validDisciplines.some(valid => d.toLowerCase().includes(valid.toLowerCase()))
    );
    
    if (competedDisciplines.length >= 2) {
      const diverseDescription = `Competes in ${competedDisciplines.length} disciplines: ${competedDisciplines.join(', ')}`;
      
      badges.push({
        id: 'diverse',
        icon: '🎭',
        name: 'Diverse',
        description: diverseDescription,
        color: '#9C27B0'
      });
    }

    // 7. Rising Star - Has 4th-6th places but no podiums
    const hasPodium = results.some(r => ['1', '2', '3'].includes(String(r.place)));
    const hasTopSix = results.some(r => ['4', '5', '6'].includes(String(r.place)));
    
    if (!hasPodium && hasTopSix) {
      badges.push({
        id: 'rising-star',
        icon: '⭐',
        name: 'Rising Star',
        description: 'Consistently placing 4th-6th',
        color: '#FFC107'
      });
    }

    return badges;
  }

  /**
   * Load ALL competition results for a dancer (not just last 365 days) for badge calculation
   */
  async loadAllTimeResultsForDancer(dancerName) {
    const allResults = [];
    
    try {
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      
      for (const docSnapshot of competitionsSnapshot.docs) {
        const comp = docSnapshot.data();
        const compId = docSnapshot.id;
        const multiplier = comp.multiplier || 1;
        
        if (comp.results && Array.isArray(comp.results)) {
          for (const result of comp.results) {
            if (result.dancerName === dancerName && !result.excluded && !result.autoExcluded && !result.invalid && !result.withdrawn) {
              const resultDate = comp.timestamp && comp.timestamp.toDate ? comp.timestamp.toDate() : new Date(comp.timestamp);
              
              allResults.push({
                compId,
                competitionName: comp.name || compId,
                points: Math.round((result.points || 0) * multiplier),
                place: result.place,
                date: resultDate,
                timestamp: comp.timestamp,
                discipline: comp.discipline,
                category: comp.category,
                dance: comp.dance || null,
                studio: result.studio || null
              });
            }
          }
        }
      }
      
      return allResults;
    } catch (error) {
      console.error(`❌ Error loading all-time results for ${dancerName}:`, error);
      return [];
    }
  }

  async calculateDancerProfile(dancerName, allCategoryRankings) {
    const profile = {
      dancerName,
      categories: {},
      studio: null, // Will be determined using proper studio resolution
      lastCalculated: serverTimestamp(),
      achievementBadges: [] // Initialize empty badges array
    };
    
    // Efficiently resolve current studio using pre-loaded data
    const currentStudio = this.resolveCurrentStudioFromTransfers(
      dancerName, 
      this.transfersByDancer, 
      this.competitionStudioMap
    );
    profile.studio = currentStudio;
    
    for (const categoryData of allCategoryRankings) {
      if (categoryData) {
        const ranking = categoryData.rankings.find(r => r.dancerName === dancerName);
        if (ranking) {
          const categoryKey = `${categoryData.discipline}_${categoryData.category}${categoryData.dance ? `_${categoryData.dance}` : ''}`;
          profile.categories[categoryKey] = {
            discipline: categoryData.discipline,
            category: categoryData.category,
            dance: categoryData.dance,
            // Basic ranking data
            currentRank: ranking.currentRank,
            totalPoints: ranking.totalPoints,
            resultsCount: ranking.resultsCount,
            top6CompIds: ranking.top6CompIds,
            // Star Ranking details
            activePoints: ranking.activePoints || ranking.totalPoints,
            currentDaysHeld: ranking.currentDaysHeld || 0,
            bestRank: ranking.bestRank || null,
            bestDaysHeld: ranking.bestDaysHeld || 0,
            hasStarRanking: ranking.hasStarRanking || false,
            totalDancers: categoryData.totalDancers || 0,
            // Timing information
            lastCalculated: ranking.lastCalculated || new Date()
          };
        }
      }
    }
    
    // Load ALL competition results for this dancer (not just last 365 days) for badge calculation
    const allTimeResults = await this.loadAllTimeResultsForDancer(dancerName);
    
    // Calculate achievement badges from all-time results
    profile.achievementBadges = this.calculateAchievementBadges(allTimeResults);
    
    // Check if dancer is an Eternal Cup winner
    profile.isEternalCupWinner = await this.checkEternalCupWinner(dancerName);
    
    return profile;
  }

  /**
   * Save all data to global cache collections
   */
  async saveGlobalCache(categoryRankings, dancerProfiles) {
    this.updateProgress({ currentTask: 'Saving to cache...' });
    
    try {
      let batch = writeBatch(db);
      let operationCount = 0;
      const maxBatchSize = 450; // Keep under Firestore's 500 limit
      
      // Save category rankings
      for (const categoryData of categoryRankings) {
        if (categoryData) {
          const categoryKey = `${categoryData.discipline}_${categoryData.category}${categoryData.dance ? `_${categoryData.dance}` : ''}`;
          const docRef = doc(db, 'globalRankingsCache', categoryKey);
          batch.set(docRef, categoryData);
          operationCount++;
          
          if (operationCount >= maxBatchSize) {
            await batch.commit();
            batch = writeBatch(db); // Create new batch after commit
            operationCount = 0;
          }
        }
      }
      
      // Commit any remaining category operations
      if (operationCount > 0) {
        await batch.commit();
      }
      
      // Save dancer profiles in batches
      batch = writeBatch(db); // Create fresh batch for dancer profiles
      operationCount = 0;
      
      for (const dancerProfile of dancerProfiles) {
        const docRef = doc(db, 'dancerRankingsCache', dancerProfile.dancerName);
        batch.set(docRef, dancerProfile);
        operationCount++;
        
        if (operationCount >= maxBatchSize) {
          await batch.commit();
          batch = writeBatch(db); // Create new batch after commit
          operationCount = 0;
        }
      }
      
      // Commit any remaining dancer operations
      if (operationCount > 0) {
        await batch.commit();
      }
      
      // Save metadata
      const metadataRef = doc(db, 'globalRankingsCache', '_metadata');
      await setDoc(metadataRef, {
        lastCalculated: serverTimestamp(),
        categoriesCount: categoryRankings.filter(c => c).length,
        dancersCount: dancerProfiles.length,
        version: '1.0'
      });
      
      console.log('✅ Global cache saved successfully');
      
    } catch (error) {
      console.error('❌ Error saving global cache:', error);
      throw error;
    }
  }

  /**
   * Main calculation function
   */
  async calculateAllRankings() {
    console.log('🚀 Starting global rankings calculation...');
    this.startTime = Date.now();
    
    try {
      // Step 0: Pre-load studio data for efficient resolution
      this.updateProgress({ currentTask: 'Loading studio transfer data...' });
      const transfersByDancer = await this.loadAllStudioTransfers();
      console.log(`🏢 Loaded studio transfers for ${transfersByDancer.size} dancers`);
      
      this.updateProgress({ currentTask: 'Building competition studio map...' });
      const competitionStudioMap = await this.buildCompetitionStudioMap();
      console.log(`📍 Built studio map for ${competitionStudioMap.size} dancers from competitions`);
      
      // Store for use in dancer profile calculation
      this.transfersByDancer = transfersByDancer;
      this.competitionStudioMap = competitionStudioMap;
      
      // Step 1: Discover all combinations
      const combinations = await this.getAllCategoryCombinations();
      
      if (combinations.length === 0) {
        throw new Error('No category combinations found');
      }
      
      // Step 2: Calculate rankings for each combination
      this.updateProgress({ currentTask: `Calculating ${combinations.length} categories...` });
      const allCategoryRankings = [];
      
      for (let i = 0; i < combinations.length; i++) {
        const combo = combinations[i];
        this.updateProgress({ 
          categories: i + 1,
          currentTask: `[${i + 1}/${combinations.length}] ${combo.discipline} ${combo.category}${combo.dance ? ` (${combo.dance})` : ''}`
        });
        
        const categoryRankings = await this.calculateCategoryRankings(combo.discipline, combo.category, combo.dance);
        allCategoryRankings.push(categoryRankings);
        
        // Small delay to be nice to Firestore
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Step 3: Get all unique dancer names from rankings AND all competitions
      const allDancerNames = new Set();
      
      // First, add dancers from rankings (those with active results in last 365 days)
      for (const categoryData of allCategoryRankings) {
        if (categoryData) {
          for (const ranking of categoryData.rankings) {
            allDancerNames.add(ranking.dancerName);
          }
        }
      }
      
      console.log(`👥 Found ${allDancerNames.size} dancers with active results (last 365 days)`);
      
      // Second, add ALL dancers from all competitions (including those without recent results)
      // This ensures dancers with studio info but no active results are still cached
      this.updateProgress({ currentTask: 'Loading all dancers from competitions...' });
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      
      competitionsSnapshot.forEach((doc) => {
        const competition = doc.data();
        if (competition.results) {
          competition.results.forEach(result => {
            if (result.dancerName) {
              allDancerNames.add(result.dancerName);
            }
          });
        }
      });
      
      const dancerNamesArray = Array.from(allDancerNames).sort();
      console.log(`👥 Found ${dancerNamesArray.length} total unique dancers (including inactive)`);
      
      // Step 4: Calculate dancer profiles
      this.updateProgress({ currentTask: 'Calculating dancer profiles...' });
      const allDancerProfiles = [];
      
      for (let i = 0; i < dancerNamesArray.length; i++) {
        const dancerName = dancerNamesArray[i];
        this.updateProgress({
          dancers: i + 1,
          currentTask: `[${i + 1}/${dancerNamesArray.length}] ${dancerName}`
        });
        
        const dancerProfile = await this.calculateDancerProfile(dancerName, allCategoryRankings);
        allDancerProfiles.push(dancerProfile);
      }
      
      // Step 5: Save everything
      await this.saveGlobalCache(allCategoryRankings, allDancerProfiles);
      
      const endTime = Date.now();
      const duration = Math.round((endTime - this.startTime) / 1000);
      
      this.updateProgress({
        currentTask: 'Completed!',
        categories: combinations.length,
        dancers: dancerNamesArray.length
      });
      
      console.log(`🎉 Completed in ${duration} seconds!`);
      console.log(`📊 ${combinations.length} categories processed`);
      console.log(`👥 ${dancerNamesArray.length} dancer profiles calculated`);
      
      return {
        success: true,
        duration,
        categoriesProcessed: combinations.length,
        dancersProcessed: dancerNamesArray.length
      };
      
    } catch (error) {
      console.error('❌ Global calculation failed:', error);
      this.updateProgress({ currentTask: `Error: ${error.message}` });
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default GlobalCacheCalculator;
